import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Users, Plus, Pencil, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", contact_name: "", contact_email: "", billing_address: "" };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try { setClients((await api.get("/clients")).data); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, contact_name: c.contact_name || "", contact_email: c.contact_email || "", billing_address: c.billing_address || "" }); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/clients/${editing.id}`, form); toast.success("Client updated"); }
      else { await api.post("/clients", form); toast.success("Client added"); }
      setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
  };
  const remove = async (c) => {
    try { await api.delete(`/clients/${c.id}`); toast.success("Client deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not delete"); }
  };

  const filtered = clients.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()) || (c.contact_name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Clients" subtitle="Manage the people and companies you work with."
        actions={<Button data-testid="clients-create-button" onClick={openNew}><Plus className="h-4 w-4 mr-2" />New client</Button>} />

      <div className="mb-4">
        <Input placeholder="Search clients..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="clients-search-input" />
      </div>

      {loading ? <Card className="p-8 text-center text-muted-foreground">Loading...</Card>
      : filtered.length === 0 ? (
        <EmptyState icon={Users} title="No clients yet" description="Add your first client to start creating jobs and invoices."
          action={<Button data-testid="clients-empty-create-button" onClick={openNew}><Plus className="h-4 w-4 mr-2" />Add client</Button>} />
      ) : (
        <Card className="overflow-hidden">
          <Table data-testid="clients-table">
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead className="hidden sm:table-cell">Contact</TableHead>
                <TableHead className="hidden md:table-cell">Added</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id} data-testid={`client-row-${c.id}`} className="hover:bg-accent/50">
                  <TableCell>
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground sm:hidden">{c.contact_name}</div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <div>{c.contact_name || "—"}</div>
                    {c.contact_email && <div className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" />{c.contact_email}</div>}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{fmtDate(c.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`client-edit-${c.id}`} onClick={() => openEdit(c)}><Pencil className="h-4 w-4" /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`client-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete {c.name}?</AlertDialogTitle><AlertDialogDescription>This cannot be undone. Clients with jobs can’t be deleted.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground" onClick={() => remove(c)}>Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle>{editing ? "Edit client" : "New client"}</SheetTitle></SheetHeader>
          <form onSubmit={save} className="space-y-4 mt-6">
            <div className="space-y-1.5"><Label>Client name *</Label><Input required value={form.name} onChange={set("name")} data-testid="client-name-input" placeholder="Acme Corp" /></div>
            <div className="space-y-1.5"><Label>Contact name</Label><Input value={form.contact_name} onChange={set("contact_name")} data-testid="client-contact-name-input" placeholder="Jane Doe" /></div>
            <div className="space-y-1.5"><Label>Contact email</Label><Input type="email" value={form.contact_email} onChange={set("contact_email")} data-testid="client-contact-email-input" placeholder="jane@acme.com" /></div>
            <div className="space-y-1.5"><Label>Billing address</Label><Textarea value={form.billing_address} onChange={set("billing_address")} data-testid="client-billing-address-input" placeholder="500 Enterprise Ave, New York" /></div>
            <SheetFooter className="pt-2">
              <Button type="submit" className="w-full" data-testid="client-save-button">{editing ? "Save changes" : "Add client"}</Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
